import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Calendar, MapPin, Phone, Shield, User } from 'lucide-react';
import type { PatientResponse } from '../../services/api/types.gen';
import { formatDateToMMDDYYYY, calculateAge } from '../../utils/dateUtils';

interface PatientHeaderProps {
  patient: PatientResponse;
  caseManagerName?: string | null;
  assignmentsLoading?: boolean;
  children?: React.ReactNode;
}

const formatDate = (dateString: string | null | undefined) =>
  formatDateToMMDDYYYY(dateString) || 'N/A';

const getAge = (dateOfBirth: string) => {
  const age = calculateAge(dateOfBirth);
  return age !== null ? age : 'Unknown';
};

export const PatientHeader: React.FC<PatientHeaderProps> = ({
  patient,
  caseManagerName,
  assignmentsLoading,
  children,
}) => {
  const navigate = useNavigate();

  return (
    <div className='border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/85 flex-shrink-0 shadow-sm'>
      <div className='px-6 py-3'>
        <div className='flex items-start justify-between gap-4'>
          <div className='flex-1 min-w-0'>
            <div className='flex items-center gap-3 mb-1'>
              <button
                onClick={() => navigate('/patients')}
                className='text-[22px] font-semibold tracking-tight text-foreground hover:text-primary transition-colors'
              >
                {patient.first_name} {patient.last_name}
              </button>
              {patient.mrn && (
                <span className='text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded'>
                  MRN: {patient.mrn}
                </span>
              )}
            </div>
            <div className='text-[13px] text-muted-foreground/90 flex flex-wrap items-center gap-x-1.5 gap-y-0.5'>
              <span className='inline-flex items-center gap-1'>
                <Calendar className='h-3.5 w-3.5' />
                DOB: {formatDate(patient.date_of_birth)}
                {patient.date_of_birth && (
                  <span className='text-xs'>
                    ({getAge(patient.date_of_birth)} yrs)
                  </span>
                )}
              </span>
              <span className='text-border'>•</span>
              <span className='inline-flex items-center gap-1'>
                <Phone className='h-3.5 w-3.5' />
                {patient.phone || 'N/A'}
              </span>
              <span className='text-border'>•</span>
              <span className='inline-flex items-center gap-1'>
                <MapPin className='h-3.5 w-3.5' />
                {patient.address || 'N/A'}
                {(patient.city || patient.state) && ', '}
                {[patient.city, patient.state].filter(Boolean).join(', ')}
              </span>
              <span className='text-border'>•</span>
              <span className='inline-flex items-center gap-1'>
                <Shield className='h-3.5 w-3.5' />
                {patient.insurance_name || 'No insurance'}
              </span>
              <span className='text-border'>•</span>
              <span className='inline-flex items-center gap-1'>
                <User className='h-3.5 w-3.5' />
                {patient.clinic_name || 'No clinic'}
              </span>
              <span className='text-border'>•</span>
              <span className='inline-flex items-center gap-1'>
                <Briefcase className='h-3.5 w-3.5' />
                <span className='font-medium text-foreground/70'>
                  Case Manager:
                </span>
                {assignmentsLoading ? '...' : caseManagerName || 'Unassigned'}
              </span>
            </div>
          </div>
        </div>
      </div>
      {children}
    </div>
  );
};
